import type { Currency } from '@repo-level/core-front/api';
import {
  API_PROXY_URL,
  API_SANDBOX_URL,
  API_URLS,
  DOMAINS,
  hostToApiMap,
  ONE_SIGNAL_IDS,
  SANDBOX_HOST,
  TEMPORARY_ENV,
} from './constants';
import { ALL_FIAT, CurrencyCode } from './currency';

const getReactAppApiUrl = () => {
  const { hostname } = window.location;

  if (hostname.includes(TEMPORARY_ENV)) {
    return API_PROXY_URL;
  }

  if (hostname.includes(SANDBOX_HOST)) {
    return API_SANDBOX_URL;
  }

  return hostToApiMap[hostname] ?? API_URLS.MAIN;
};

export const REACT_APP_API_URL = getReactAppApiUrl();

const getPaymentsLibUrl = (hostname: string = window.location.hostname) => {
  if (hostname.endsWith(SANDBOX_HOST)) {
    return 'https://payments-lib.cdn.s7s.ai/v1/index.js';
  }

  const apiUrl = hostToApiMap[hostname] || API_URLS.MAIN;
  return `${apiUrl}/payments-lib/v1/index.js`;
};

export const PAYMENTS_LIB_URL = getPaymentsLibUrl(window.location.hostname);

const MAIN_SITE_DOMAINS = Object.entries(DOMAINS)
  .filter(([key]) => key === 'MAIN' || key.startsWith('MIRROR_'))
  .map(([, domain]) => domain);

// Canonical origin: main + mirrors (± `www.`) point to the main domain.
export const getSiteOrigin = (
  { hostname, origin }: Pick<Location, 'hostname' | 'origin'> = window.location
) => {
  const host = hostname.replace(/^www\./, '');

  return MAIN_SITE_DOMAINS.includes(host) ? `https://${DOMAINS.MAIN}` : origin;
};

export const SITE_ORIGIN = getSiteOrigin();

export const BQ_API_URL = window.appConfig.BQ_API_URL || '';
export const JACKPOT_API_URL = window.appConfig.JACKPOT_API_URL || '';
export const CMS_URL = window.appConfig.CMS_URL || '';
export const CMS_VERSION = window.appConfig.CMS_VERSION || 'v4';
export const isCMSV3 = CMS_VERSION === 'v3';

export enum Country {
  WorldWide = 'WW',
  // START countries for translations
  Germany = 'DE',
  Canada = 'CA',
  Italy = 'IT',
  Norway = 'NO',
  Portugal = 'PT',
  Brazil = 'BR',
  Australia = 'AU',
  Ireland = 'IE',
  NewZealand = 'NZ',
  // END countries for translations

  Switzerland = 'CH',
  Austria = 'AT',
  Azerbaijan = 'AZ',
  Bangladesh = 'BD',
  Belarus = 'BY',
  Bulgaria = 'BG',
  Croatia = 'HR',
  Cyprus = 'CY',
  Finland = 'FI',
  India = 'IN',
  Latvia = 'LV',
  Malaysia = 'MY',
  Macedonia = 'MK',
  Moldova = 'MD',
  Romania = 'RO',
  Russia = 'RU',
  Poland = 'PL',
  UnitedStates = 'US',
  UnitedKingdom = 'GB',
  Ukraine = 'UA',
  Slovakia = 'SK',
  Spain = 'ES',
  France = 'FR',
  Guadeloupe = 'GP',
  Martinique = 'MQ',
  FrenchGuiana = 'GF',
  Reunion = 'RE',
  Mayotte = 'YT',
  StMartin = 'MF',
  FrenchPolynesia = 'PF',
  WallisAndFutuna = 'WF',
  NewCaledonia = 'NC',
  Netherlands = 'NL',
  Israel = 'IL',
  Lithuania = 'LT',
  Curacao = 'CW',
  Gibraltar = 'GI',
  Jersey = 'JE',
  Greece = 'GR',
  Belgium = 'BE',
  Hungary = 'HU',
  Afghanistan = 'AF',
  CzechRepublic = 'CZ',
  Serbia = 'RS',
  Syria = 'SY',
  NorthKorea = 'KP',
  Iran = 'IR',
  Cuba = 'CU',
  Turkey = 'TR',
  Jamaica = 'JM',
  Japan = 'JP',
  Georgia = 'GE',
  Angola = 'AO',
  Albania = 'AL',
  Iraq = 'IQ',
  Uganda = 'UG',
  Pakistan = 'PK',
  Panama = 'PA',
  Lebanon = 'LB',
  Zimbabwe = 'ZW',
  Mauritius = 'MU',
  Nicaragua = 'NI',
  Yemen = 'YE',
  CentralAfricanRepublic = 'CF',
  CotedIvoire = 'CI',
  Sudan = 'SD',
  Liberia = 'LR',
  CaymanIslands = 'KY',
  Somalia = 'SO',
  RepublicOfTheCongo = 'CG',
  DemocraticRepublicOfTheCongo = 'CD',
  Eritrea = 'ER',
  Haiti = 'HT',
  SierraLeone = 'SL',
  Ethiopia = 'ET',
  Myanmar = 'MM',
  SouthSudan = 'SS',
  BurkinaFaso = 'BF',
  Libya = 'LY',
  Mali = 'ML',
  Barbados = 'BB',
  Rwanda = 'RW',
  Estonia = 'EE',
  BonaireSintEustatiusAndSaba = 'BQ',
  // updated
  AlandIslands = 'AX',
  NorfolkIsland = 'NF',
  Bahrain = 'BH',
  Mongolia = 'MN',
  Thailand = 'TH',
  HongKong = 'HK',
  Nigeria = 'NG',
  Antarctica = 'AQ',
  Tanzania = 'TZ',
  Tunisia = 'TN',
  Chad = 'TD',
  Honduras = 'HN',
  Andorra = 'AD',
  TrinidadAndTobago = 'TT',
  Jordan = 'JO',
  Egypt = 'EG',
  Guam = 'GU',
  Bahamas = 'BS',
  WesternSahara = 'EH',
  BosniaAndHerzegovina = 'BA',
  Monaco = 'MC',
  Singapore = 'SG',
  Cambodia = 'KH',
  ElSalvador = 'SV',
  Liechtenstein = 'LI',
  BritishIndianOceanTerritory = 'IO',
  Fiji = 'FJ',
  China = 'CN',
  ChristmasIsland = 'CX',
  Bolivia = 'BO',
  Greenland = 'GL',
  FaroeIslands = 'FO',
  Palestine = 'PS',
  Indonesia = 'ID',
  Aruba = 'AW',
  Benin = 'BJ',
  Bermuda = 'BM',
  PapuaNewGuinea = 'PG',
  Seychelles = 'SC',
  SouthGeorgiaAndTheSouthSandwichIslands = 'GS',
  Vietnam = 'VN',
  Taiwan = 'TW',
  Ecuador = 'EC',
  SanMarino = 'SM',
  Samoa = 'WS',
  Guatemala = 'GT',
  HolySee = 'VA',
  LaoPeoplesDemocraticRepublic = 'LA',
  DominicanRepublic = 'DO',
  CookIslands = 'CK',
  Tonga = 'TO',
  Guernsey = 'GG',
  Vanuatu = 'VU',
  Belize = 'BZ',
  Montenegro = 'ME',
  Oman = 'OM',
  SaintKittsAndNevis = 'KN',
  Niger = 'NE',
  Gabon = 'GA',
  Swaziland = 'SZ',
  CongoDemocraticRepublicOfTheCongo = 'CD',
  Ghana = 'GH',
  Dominica = 'DM',
  Burundi = 'BI',
  BouvetIsland = 'BV',
  Togo = 'TG',
  NorthernMarianaIslands = 'MP',
  Tokelau = 'TK',
  Suriname = 'SR',
  SaintLucia = 'LC',
  SriLanka = 'LK',
  Zambia = 'ZM',
  Paraguay = 'PY',
  Botswana = 'BW',
  SaintMartin = 'MF',
  Djibouti = 'DJ',
  IsleOfMan = 'IM',
  SaintPierreAndMiquelon = 'PM',
  SolomonIslands = 'SB',
  FalklandIslands = 'FK',
  Senegal = 'SN',
  Cameroon = 'CM',
  CaboVerde = 'CV',
  Kenya = 'KE',
  Kyrgyzstan = 'KG',
  Anguilla = 'AI',
  Tuvalu = 'TV',
  CocosIslands = 'CC',
  AntiguaAndBarbuda = 'AG',
  Guinea = 'GN',
  BruneiDarussalam = 'BN',
  Bonaire = 'BQ',
  Tajikistan = 'TJ',
  SaoTomeAndPrincipe = 'ST',
  Madagascar = 'MG',
  HeardIslandAndMcDonaldIslands = 'HM',
  Montserrat = 'MS',
  Mozambique = 'MZ',
  Nepal = 'NP',
  Guyana = 'GY',
  GuineaBissau = 'GW',
  Nauru = 'NR',
  TurksAndCaicosIslands = 'TC',
  Bhutan = 'BT',
  Namibia = 'NA',
  VirginIslands = 'VI',
  Kiribati = 'KI',
  Malawi = 'MW',
  EquatorialGuinea = 'GQ',
  SaintHelena = 'SH',
  SaintBarthelemy = 'BL',
  SyrianArabRepublic = 'SY',
  Pitcairn = 'PN',
  UnitedStatesMinorOutlyingIslands = 'UM',
  Palau = 'PW',
  Gambia = 'GM',
  Niue = 'NU',
  Maldives = 'MV',
  Macao = 'MO',
  Turkmenistan = 'TM',
  Grenada = 'GD',
  Micronesia = 'FM',
  TimorLeste = 'TL',
  SintMaarten = 'SX',
  Mauritania = 'MR',
  MarshallIslands = 'MH',
  Comoros = 'KM',
  Algeria = 'DZ',
  Armenia = 'AM',
  Philippines = 'PH',
  SvalbardAndJanMayen = 'SJ',
  AmericanSamoa = 'AS',
  SaudiArabia = 'SA',
  Sweden = 'SE',
  Iceland = 'IS',
  SouthKorea = 'KR',
  Qatar = 'QA',
  Morocco = 'MA',
  CostaRica = 'CR',
  Uzbekistan = 'UZ',
  Venezuela = 'VE',
  Colombia = 'CO',
  DutchWestIndies = 'SX',
  MayotteStMartin = 'YT',
  Mexico = 'MX',
  Argentina = 'AR',
  Uruguay = 'UY',
  Chile = 'CL',
  PuertoRico = 'PR',
  Peru = 'PE',
  Kuwait = 'KW',
  UnitedArabEmirates = 'AE',
  Kazakhstan = 'KZ',
  Denmark = 'DK',
  Malta = 'MT',
  Slovenia = 'SI',
  Kosovo = 'XK',
}

export const NOT_ALLOWED_COUNTRIES: { [codeCountry: string]: string } = {
  [Country.France]: 'France',
  [Country.UnitedKingdom]: 'United Kingdom',
  [Country.Poland]: 'Poland',
  [Country.Bangladesh]: 'Bangladesh',
  [Country.Brazil]: 'Brazil',
  [Country.Moldova]: 'Moldova',
  [Country.Azerbaijan]: 'Azerbaijan',
  [Country.Macedonia]: 'Macedonia',
  [Country.Israel]: 'Israel',
  [Country.Croatia]: 'Croatia',
  [Country.Romania]: 'Romania',
  [Country.India]: 'India',
  [Country.Latvia]: 'Latvia',
  [Country.Gibraltar]: 'Gibraltar',
  [Country.Bulgaria]: 'Bulgaria',
  [Country.Guadeloupe]: 'Guadeloupe',
  [Country.Jersey]: 'Jersey',
  [Country.Malaysia]: 'Malaysia',
  [Country.Spain]: 'Spain',
  [Country.Belarus]: 'Belarus',
  [Country.Curacao]: 'Curacao',
  [Country.Cyprus]: 'Cyprus',
  [Country.FrenchGuiana]: 'French Guiana',
  [Country.FrenchPolynesia]: 'French Polynesia',
  [Country.Martinique]: 'Martinique',
  [Country.Mayotte]: 'Mayotte',
  [Country.NewCaledonia]: 'New Caledonia',
  [Country.Reunion]: 'Reunion',
  [Country.Russia]: 'Russia',
  [Country.StMartin]: 'StMartin',
  [Country.WallisAndFutuna]: 'Wallis and Futuna',
  [Country.Greece]: 'Greece',
  [Country.Netherlands]: 'Netherlands',
  [Country.Ukraine]: 'Ukraine',
  [Country.Belgium]: 'Belgium',
  [Country.UnitedStates]: 'United States',
  [Country.Afghanistan]: 'Afghanistan',
  [Country.CzechRepublic]: 'Czech Republic',
  [Country.Serbia]: 'Serbia',
  [Country.Portugal]: 'Portugal',
  [Country.Syria]: 'Syria',
  [Country.NorthKorea]: 'North Korea',
  [Country.Iran]: 'Iran',
  [Country.Cuba]: 'Cuba',
  [Country.Turkey]: 'Turkey',
  [Country.Jamaica]: 'Jamaica',
  [Country.Slovakia]: 'Slovakia',
  [Country.Lithuania]: 'Lithuania',
  [Country.Georgia]: 'Georgia',
  [Country.Albania]: 'Albania',
  [Country.Angola]: 'Angola',
  [Country.Iraq]: 'Iraq',
  [Country.Uganda]: 'Uganda',
  [Country.Pakistan]: 'Pakistan',
  [Country.Panama]: 'Panama',
  [Country.Lebanon]: 'Lebanon',
  [Country.Zimbabwe]: 'Zimbabwe',
  [Country.Mauritius]: 'Mauritius',
  [Country.Nicaragua]: 'Nicaragua',
  [Country.Yemen]: 'Yemen',
  [Country.CentralAfricanRepublic]: 'Central African Republic',
  [Country.CotedIvoire]: '"Côte Divoire"',
  [Country.Sudan]: 'Sudan',
  [Country.Liberia]: 'Liberia',
  [Country.CaymanIslands]: 'Cayman Islands',
  [Country.Somalia]: 'Somalia',
  [Country.RepublicOfTheCongo]: 'Republic of the Congo',
  [Country.DemocraticRepublicOfTheCongo]: 'Democratic Republic of the Congo',
  [Country.Eritrea]: 'Eritrea',
  [Country.Haiti]: 'Haiti',
  [Country.SierraLeone]: 'Sierra Leone',
  [Country.Ethiopia]: 'Ethiopia',
  [Country.Myanmar]: 'Myanmar',
  [Country.SouthSudan]: 'South Sudan',
  [Country.BurkinaFaso]: 'Burkina Faso',
  [Country.Libya]: 'Libya',
  [Country.Mali]: 'Mali',
  [Country.Barbados]: 'Barbados',
  [Country.Rwanda]: 'Rwanda',
  [Country.Hungary]: 'Hungary',
  [Country.Estonia]: 'Estonia',
  [Country.AlandIslands]: 'Aland Islands',
  [Country.NorfolkIsland]: 'Norfolk Island',
  [Country.Bahrain]: 'Bahrain',
  [Country.Mongolia]: 'Mongolia',
  [Country.Thailand]: 'Thailand',
  [Country.HongKong]: 'Hong Kong',
  [Country.Nigeria]: 'Nigeria',
  [Country.Antarctica]: 'Antarctica',
  [Country.Tanzania]: 'Tanzania',
  [Country.Tunisia]: 'Tunisia',
  [Country.Chad]: 'Chad',
  [Country.Honduras]: 'Honduras',
  [Country.Andorra]: 'Andorra',
  [Country.TrinidadAndTobago]: 'Trinidad and Tobago',
  [Country.Japan]: 'Japan',
  [Country.Jordan]: 'Jordan',
  [Country.Egypt]: 'Egypt',
  [Country.Guam]: 'Guam',
  [Country.Bahamas]: 'Bahamas',
  [Country.WesternSahara]: 'Western Sahara',
  [Country.BosniaAndHerzegovina]: 'Bosnia and Herzegovina',
  [Country.Monaco]: 'Monaco',
  [Country.Singapore]: 'Singapore',
  [Country.Cambodia]: 'Cambodia',
  [Country.ElSalvador]: 'El Salvador',
  [Country.Liechtenstein]: 'Liechtenstein',
  [Country.BritishIndianOceanTerritory]: 'British Indian Ocean Territory',
  [Country.Fiji]: 'Fiji',
  [Country.China]: 'China',
  [Country.ChristmasIsland]: 'Christmas Island',
  [Country.Bolivia]: 'Bolivia',
  [Country.Greenland]: 'Greenland',
  [Country.FaroeIslands]: 'Faroe Islands',
  [Country.Palestine]: 'Palestine',
  [Country.Indonesia]: 'Indonesia',
  [Country.Aruba]: 'Aruba',
  [Country.Benin]: 'Benin',
  [Country.Bermuda]: 'Bermuda',
  [Country.PapuaNewGuinea]: 'Papua New Guinea',
  [Country.Seychelles]: 'Seychelles',
  [Country.SouthGeorgiaAndTheSouthSandwichIslands]:
    'South Georgia and the South Sandwich Islands',
  [Country.Vietnam]: 'Vietnam',
  [Country.Taiwan]: 'Taiwan',
  [Country.Ecuador]: 'Ecuador',
  [Country.SanMarino]: 'San Marino',
  [Country.Samoa]: 'Samoa',
  [Country.Guatemala]: 'Guatemala',
  [Country.HolySee]: 'Holy See',
  [Country.LaoPeoplesDemocraticRepublic]: "Lao People's Democratic Republic",
  [Country.DominicanRepublic]: 'Dominican Republic',
  [Country.CookIslands]: 'Cook Islands',
  [Country.Tonga]: 'Tonga',
  [Country.Guernsey]: 'Guernsey',
  [Country.Vanuatu]: 'Vanuatu',
  [Country.Belize]: 'Belize',
  [Country.Montenegro]: 'Montenegro',
  [Country.Oman]: 'Oman',
  [Country.SaintKittsAndNevis]: 'Saint Kitts and Nevis',
  [Country.Niger]: 'Niger',
  [Country.Gabon]: 'Gabon',
  [Country.Swaziland]: 'Eswatini',
  [Country.CongoDemocraticRepublicOfTheCongo]:
    'Democratic Republic of the Congo',
  [Country.Ghana]: 'Ghana',
  [Country.Dominica]: 'Dominica',
  [Country.Burundi]: 'Burundi',
  [Country.BouvetIsland]: 'Bouvet Island',
  [Country.Togo]: 'Togo',
  [Country.NorthernMarianaIslands]: 'Northern Mariana Islands',
  [Country.Tokelau]: 'Tokelau',
  [Country.Suriname]: 'Suriname',
  [Country.SaintLucia]: 'Saint Lucia',
  [Country.SriLanka]: 'Sri Lanka',
  [Country.Zambia]: 'Zambia',
  [Country.Paraguay]: 'Paraguay',
  [Country.Botswana]: 'Botswana',
  [Country.SaintMartin]: 'Saint Martin',
  [Country.Djibouti]: 'Djibouti',
  [Country.IsleOfMan]: 'Isle of Man',
  [Country.SaintPierreAndMiquelon]: 'Saint Pierre and Miquelon',
  [Country.SolomonIslands]: 'Solomon Islands',
  [Country.FalklandIslands]: 'Falkland Islands',
  [Country.Senegal]: 'Senegal',
  [Country.Cameroon]: 'Cameroon',
  [Country.CaboVerde]: 'Cabo Verde',
  [Country.Kenya]: 'Kenya',
  [Country.Kyrgyzstan]: 'Kyrgyzstan',
  [Country.Anguilla]: 'Anguilla',
  [Country.Tuvalu]: 'Tuvalu',
  [Country.CocosIslands]: 'Cocos (Keeling) Islands',
  [Country.AntiguaAndBarbuda]: 'Antigua and Barbuda',
  [Country.Guinea]: 'Guinea',
  [Country.BruneiDarussalam]: 'Brunei Darussalam',
  [Country.Bonaire]: 'Bonaire',
  [Country.Tajikistan]: 'Tajikistan',
  [Country.SaoTomeAndPrincipe]: 'Sao Tome and Principe',
  [Country.Madagascar]: 'Madagascar',
  [Country.HeardIslandAndMcDonaldIslands]: 'Heard Island and McDonald Islands',
  [Country.Montserrat]: 'Montserrat',
  [Country.Mozambique]: 'Mozambique',
  [Country.Nepal]: 'Nepal',
  [Country.Guyana]: 'Guyana',
  [Country.GuineaBissau]: 'Guinea-Bissau',
  [Country.Nauru]: 'Nauru',
  [Country.TurksAndCaicosIslands]: 'Turks and Caicos Islands',
  [Country.Bhutan]: 'Bhutan',
  [Country.Namibia]: 'Namibia',
  [Country.VirginIslands]: 'Virgin Islands',
  [Country.Kiribati]: 'Kiribati',
  [Country.Malawi]: 'Malawi',
  [Country.EquatorialGuinea]: 'Equatorial Guinea',
  [Country.SaintHelena]: 'Saint Helena',
  [Country.SaintBarthelemy]: 'Saint Barthélemy',
  [Country.SyrianArabRepublic]: 'Syrian Arab Republic',
  [Country.Pitcairn]: 'Pitcairn',
  [Country.UnitedStatesMinorOutlyingIslands]:
    'United States Minor Outlying Islands',
  [Country.Palau]: 'Palau',
  [Country.Gambia]: 'Gambia',
  [Country.Niue]: 'Niue',
  [Country.Maldives]: 'Maldives',
  [Country.Macao]: 'Macao',
  [Country.Turkmenistan]: 'Turkmenistan',
  [Country.Grenada]: 'Grenada',
  [Country.Micronesia]: 'Micronesia',
  [Country.TimorLeste]: 'Timor-Leste',
  [Country.SintMaarten]: 'Sint Maarten',
  [Country.Mauritania]: 'Mauritania',
  [Country.MarshallIslands]: 'Marshall Islands',
  [Country.Comoros]: 'Comoros',
  [Country.Algeria]: 'Algeria',
  [Country.Armenia]: 'Armenia',
  [Country.Philippines]: 'Philippines',
  [Country.SvalbardAndJanMayen]: 'Svalbard and Jan Mayen',
  [Country.AmericanSamoa]: 'American Samoa',
  [Country.SaudiArabia]: 'Saudi Arabia',
  [Country.Sweden]: 'Sweden',
  [Country.Iceland]: 'Iceland',
  [Country.SouthKorea]: 'South Korea',
  [Country.Qatar]: 'Qatar',
  [Country.Morocco]: 'Morocco',
  [Country.CostaRica]: 'Costa Rica',
  [Country.Uzbekistan]: 'Uzbekistan',
  [Country.Venezuela]: 'Venezuela',
  [Country.Colombia]: 'Colombia',
  [Country.DutchWestIndies]: 'Dutch West Indies',
  [Country.MayotteStMartin]: 'Mayotte/St. Martin',
};

export const COUNTRIES: { [codeCountry: string]: string } = {
  ...NOT_ALLOWED_COUNTRIES,
};

export const HIDDEN_COUNTRIES = [
  Country.Brazil,
  Country.Malta,
  Country.Japan,
  Country.CongoDemocraticRepublicOfTheCongo,
];

export const BONUS_RESTRICTED_COUNTRIES = [
  Country.Afghanistan,
  Country.Albania,
  Country.Azerbaijan,
  Country.Bangladesh,
  Country.Belarus,
  Country.Bulgaria,
  Country.Croatia,
  Country.Cyprus,
  Country.Estonia,
  Country.Finland,
  Country.Georgia,
  Country.India,
  Country.Iran,
  Country.Iraq,
  Country.Israel,
  Country.Latvia,
  Country.Lithuania,
  Country.Malaysia,
  Country.Pakistan,
  Country.Macedonia,
  Country.Moldova,
  Country.Romania,
  Country.Serbia,
  Country.Turkey,
  Country.Hungary,
  Country.UnitedStates,
  Country.UnitedKingdom,
  Country.Ukraine,
  Country.Spain,
  Country.Guadeloupe,
  Country.FrenchGuiana,
  Country.Martinique,
  Country.Mayotte,
  Country.FrenchPolynesia,
  Country.Netherlands,
  Country.Israel,
  Country.Gibraltar,
  Country.Greece,
  Country.Portugal,
  Country.Syria,
  Country.NorthKorea,
  Country.Cuba,
  Country.Jamaica,
  Country.Uganda,
  Country.Panama,
  Country.Lebanon,
  Country.Zimbabwe,
  Country.Mauritius,
  Country.Nicaragua,
  Country.Yemen,
  Country.CentralAfricanRepublic,
  Country.Sudan,
  Country.Liberia,
  Country.CaymanIslands,
  Country.Somalia,
  Country.RepublicOfTheCongo,
  Country.DemocraticRepublicOfTheCongo,
  Country.Eritrea,
  Country.Haiti,
  Country.CotedIvoire,
  Country.SierraLeone,
  Country.Ethiopia,
  Country.Myanmar,
  Country.SouthSudan,
  Country.BurkinaFaso,
  Country.Libya,
  Country.Mali,
  Country.Barbados,
  Country.Rwanda,
  Country.CzechRepublic,
  Country.Curacao,
  Country.Jersey,
  Country.NewCaledonia,
  Country.StMartin,
  Country.Reunion,
  Country.Cyprus,
  Country.Sweden,
  Country.WallisAndFutuna,
  Country.Aruba,
  Country.BonaireSintEustatiusAndSaba,
];

export const MAP_LOCALE_DOMAIN: {
  [countryCode: string]: { from: string; to: string };
} = {
  [Country.Australia]: { from: DOMAINS.MAIN, to: DOMAINS.MIRROR_1 },
};

export const DEFAULT_CURRENCIES: Currency[] = [
  {
    code: CurrencyCode.AUD,
    symbol: 'A$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.EUR,
    symbol: '€',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.USD,
    symbol: '$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.BTC,
    symbol: 'BTC',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: CurrencyCode.MBTC,
        symbol: 'mBTC',
        subunitsToUnit: 100_000,
      },
      {
        code: 'μBTC',
        symbol: 'μBTC',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.NZD,
    symbol: 'NZ$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.CAD,
    symbol: 'C$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.ETH,
    symbol: 'ETH',
    subunitsToUnit: 1_000_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mETH',
        symbol: 'mETH',
        subunitsToUnit: 1_000_000,
      },
      {
        code: 'μETH',
        symbol: 'μETH',
        subunitsToUnit: 1000,
      },
    ],
  },
  {
    code: CurrencyCode.USDT,
    symbol: 'USDT',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.NOK,
    symbol: 'kr',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.LTC,
    symbol: 'LTC',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mLTC',
        symbol: 'mLTC',
        subunitsToUnit: 100_000,
      },
      {
        code: 'μLTC',
        symbol: 'μLTC',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.DOG,
    symbol: 'DOGE',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.BCH,
    symbol: 'BCH',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mBCH',
        symbol: 'mBCH',
        subunitsToUnit: 100_000,
      },
      {
        code: 'μBCH',
        symbol: 'μBCH',
        subunitsToUnit: 100,
      },
    ],
  },
];

export const MAP_CURRENCY_RESTRICTIONS: { [country: string]: string[] } = {
  [Country.Mexico]: ALL_FIAT,
  [Country.Argentina]: ALL_FIAT,
  [Country.Uruguay]: ALL_FIAT,
  [Country.Chile]: ALL_FIAT,
  [Country.PuertoRico]: ALL_FIAT,
  [Country.Peru]: ALL_FIAT,
};

export const MAP_COUNTRY_CURRENCY: { [country: string]: string } = {
  [Country.Australia]: CurrencyCode.AUD,
  [Country.NewZealand]: CurrencyCode.NZD,
  [Country.Canada]: CurrencyCode.CAD,
  [Country.Norway]: CurrencyCode.NOK,
  [Country.Kuwait]: CurrencyCode.USD,
  [Country.UnitedArabEmirates]: CurrencyCode.USD,
  [Country.Kazakhstan]: CurrencyCode.USD,
  [Country.Austria]: CurrencyCode.EUR,
  [Country.Belgium]: CurrencyCode.EUR,
  [Country.Bulgaria]: CurrencyCode.EUR,
  [Country.Croatia]: CurrencyCode.EUR,
  [Country.Cyprus]: CurrencyCode.EUR,
  [Country.CzechRepublic]: CurrencyCode.EUR,
  [Country.Denmark]: CurrencyCode.EUR,
  [Country.Estonia]: CurrencyCode.EUR,
  [Country.Finland]: CurrencyCode.EUR,
  [Country.France]: CurrencyCode.EUR,
  [Country.Germany]: CurrencyCode.EUR,
  [Country.Greece]: CurrencyCode.EUR,
  [Country.Hungary]: CurrencyCode.EUR,
  [Country.Ireland]: CurrencyCode.EUR,
  [Country.Italy]: CurrencyCode.EUR,
  [Country.Latvia]: CurrencyCode.EUR,
  [Country.Lithuania]: CurrencyCode.EUR,
  [Country.Malta]: CurrencyCode.EUR,
  [Country.Netherlands]: CurrencyCode.EUR,
  [Country.Poland]: CurrencyCode.EUR,
  [Country.Portugal]: CurrencyCode.EUR,
  [Country.Romania]: CurrencyCode.EUR,
  [Country.Slovakia]: CurrencyCode.EUR,
  [Country.Slovenia]: CurrencyCode.EUR,
  [Country.Spain]: CurrencyCode.EUR,
  [Country.Sweden]: CurrencyCode.EUR,
  [Country.Switzerland]: CurrencyCode.EUR,
  [Country.Andorra]: CurrencyCode.EUR,
  [Country.Kosovo]: CurrencyCode.EUR,
  [Country.Monaco]: CurrencyCode.EUR,
  [Country.Montenegro]: CurrencyCode.EUR,
  [Country.HolySee]: CurrencyCode.EUR,
  [Country.SanMarino]: CurrencyCode.EUR,
  [Country.Brazil]: CurrencyCode.BRL,
  [Country.Mexico]: CurrencyCode.BTC,
  [Country.Argentina]: CurrencyCode.BTC,
  [Country.Uruguay]: CurrencyCode.BTC,
  [Country.Chile]: CurrencyCode.BTC,
  [Country.PuertoRico]: CurrencyCode.BTC,
  [Country.Peru]: CurrencyCode.BTC,
  default: CurrencyCode.USD,
};

export const MAP_LOCALE_CURRENCY_CODE: { [locale: string]: string } = {
  en: CurrencyCode.USD,
  de: CurrencyCode.EUR,
  'en-CA': CurrencyCode.CAD,
  'fr-CA': CurrencyCode.CAD,
  'fr-CH': CurrencyCode.EUR,
  fr: CurrencyCode.EUR,
  it: CurrencyCode.EUR,
  nn: CurrencyCode.NOK,
  no: CurrencyCode.NOK,
  pt: CurrencyCode.EUR,
  'pt-BR': CurrencyCode.BRL,
  'en-AU': CurrencyCode.AUD,
  'en-IE': CurrencyCode.EUR,
  'en-NZ': CurrencyCode.NZD,
  sl: CurrencyCode.EUR,
};

export const DE_LOCALE_COUNTRIES = [Country.Austria, Country.Switzerland];

export const STAG_PARAMS = [
  'stag',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref_code',
];

export const CROSS_SALE_STAGS = ['131914', '193341'];

export type RangeIP = [from: string, to: string];

const rangeIP = (from: string, to: string): RangeIP => [from, to];

export const BLACK_LIST_IPS: RangeIP[] = [
  rangeIP('195.37.175.16', '195.37.175.23'),
  rangeIP('195.37.175.16', '195.37.175.23'),
  rangeIP('176.94.185.176', '176.94.185.183'),
  rangeIP('195.243.21.200', '195.243.21.207'),
  rangeIP('178.13.67.160', '178.13.67.167'),
  rangeIP('195.243.128.32', '195.243.128.47'),
  rangeIP('213.128.119.112', '213.128.119.115'),
  rangeIP('194.196.76.0', '194.196.76.255'),
  rangeIP('213.139.133.144', '213.139.133.159'),
  rangeIP('193.175.51.0', '193.175.51.255'),
  rangeIP('176.94.185.48', '176.94.185.55'),
  rangeIP('213.211.242.224', '213.211.242.231'),
  rangeIP('217.239.183.0', '217.239.183.7'),
  rangeIP('217.239.232.88', '217.239.232.95'),
  rangeIP('213.139.136.16', '213.139.136.31'),
  rangeIP('217.239.228.48', '217.239.228.55'),
  rangeIP('217.239.175.208', '217.239.175.215'),
  rangeIP('213.211.236.128', '213.211.236.135'),
  rangeIP('213.211.237.32', '213.211.237.47'),
  rangeIP('217.239.161.248', '217.239.161.255'),
  rangeIP('217.239.179.240', '217.239.179.247'),
  rangeIP('217.89.43.24', '217.89.43.31'),
  rangeIP('62.153.239.96', '62.153.239.111'),
  rangeIP('62.153.249.208', '62.153.249.223'),
  rangeIP('217.239.241.104', '217.239.241.111'),
  rangeIP('217.89.190.208', '217.89.190.215'),
  rangeIP('62.153.192.0', '62.153.192.7'),
  rangeIP('217.239.233.88', '217.239.233.95'),
  rangeIP('62.153.119.212', '62.153.119.215'),
  rangeIP('62.154.181.224', '62.154.181.239'),
  rangeIP('217.89.66.8', '217.89.66.15'),
  rangeIP('62.157.120.160', '62.157.120.167'),
  rangeIP('62.159.151.192', '62.159.151.207'),
  rangeIP('62.27.36.0', '62.27.36.3'),
  rangeIP('62.157.237.176', '62.157.237.191'),
  rangeIP('62.157.241.32', '62.157.241.39'),
  rangeIP('80.149.210.152', '80.149.210.159'),
  rangeIP('62.225.102.128', '62.225.102.143'),
  rangeIP('80.146.161.0', '80.146.161.31'),
  rangeIP('80.154.205.16', '80.154.205.23'),
  rangeIP('80.155.151.168', '80.155.151.175'),
  rangeIP('87.129.146.200', '87.129.146.207'),
  rangeIP('81.14.181.64', '81.14.181.71'),
  rangeIP('80.155.17.88', '80.155.17.95'),
  rangeIP('81.14.186.80', '81.14.186.95'),
  rangeIP('82.195.76.120', '82.195.76.127'),
  rangeIP('82.195.68.0', '82.195.69.255'),
  rangeIP('80.155.47.200', '80.155.47.207'),
  rangeIP('87.129.136.64', '87.129.136.79'),
  rangeIP('85.126.148.176', '85.126.148.183'),
  rangeIP('82.195.76.192', '82.195.76.223'),
  rangeIP('87.129.216.96', '87.129.216.127'),
  rangeIP('87.129.199.96', '87.129.199.103'),
  rangeIP('87.129.31.8', '87.129.31.15'),
  rangeIP('87.129.41.200', '87.129.41.207'),
  rangeIP('87.129.180.248', '87.129.180.255'),
  rangeIP('87.129.25.8', '87.129.25.15'),
  rangeIP('87.129.33.40', '87.129.33.47'),
  rangeIP('87.129.149.72', '87.129.149.79'),
  rangeIP('87.129.222.200', '87.129.222.207'),
  rangeIP('87.129.4.0', '87.129.4.7'),
  rangeIP('87.129.63.208', '87.129.63.215'),
  rangeIP('87.129.59.56', '87.129.59.63'),
  rangeIP('87.130.104.0', '87.130.104.15'),
  rangeIP('87.130.1.168', '87.130.1.175'),
  rangeIP('87.130.126.16', '87.130.126.31'),
  rangeIP('87.130.1.176', '87.130.1.183'),
  rangeIP('87.130.127.0', '87.130.127.7'),
  rangeIP('87.130.1.216', '87.130.1.223'),
  rangeIP('87.129.5.0', '87.129.5.7'),
  rangeIP('87.129.59.8', '87.129.59.15'),
  rangeIP('87.190.33.48', '87.190.33.55'),
  rangeIP('91.25.132.232', '91.25.132.239'),
  rangeIP('87.130.62.120', '87.130.62.127'),
  rangeIP('87.130.73.48', '87.130.73.55'),
  rangeIP('87.190.50.104', '87.190.50.111'),
  rangeIP('88.79.215.192', '88.79.215.199'),
  rangeIP('87.130.34.40', '87.130.34.47'),
  rangeIP('91.25.113.88', '91.25.113.95'),
  rangeIP('87.191.32.248', '87.191.32.255'),
  rangeIP('87.191.41.176', '87.191.41.183'),
  rangeIP('91.25.153.104', '91.25.153.111'),
  rangeIP('91.25.159.224', '91.25.159.231'),
  rangeIP('91.25.139.176', '91.25.139.183'),
  rangeIP('91.25.144.24', '91.25.144.31'),
  rangeIP('91.25.148.224', '91.25.148.231'),
  rangeIP('91.25.150.88', '91.25.150.95'),
  rangeIP('91.25.159.48', '91.25.159.55'),
  rangeIP('91.25.143.88', '91.25.143.95'),
  rangeIP('91.25.140.104', '91.25.140.111'),
  rangeIP('91.25.148.80', '91.25.148.87'),
  rangeIP('91.25.182.56', '91.25.182.63'),
  rangeIP('91.26.20.224', '91.26.20.231'),
  rangeIP('91.26.82.192', '91.26.82.199'),
  rangeIP('91.25.207.32', '91.25.207.39'),
  rangeIP('91.26.102.216', '91.26.102.223'),
  rangeIP('91.25.174.80', '91.25.174.87'),
  rangeIP('91.26.71.248', '91.26.71.255'),
  rangeIP('91.25.254.192', '91.25.254.199'),
  rangeIP('91.25.170.184', '91.25.170.191'),
  rangeIP('91.25.210.64', '91.25.210.71'),
  rangeIP('176.95.153.192', '176.95.153.199'),
  rangeIP('178.15.142.128', '178.15.142.143'),
  rangeIP('178.15.142.208', '178.15.142.223'),
  rangeIP('193.134.4.0', '193.134.4.255'),
  rangeIP('194.11.240.0', '194.11.241.255'),
  rangeIP('145.253.232.16', '145.253.232.31'),
  rangeIP('188.20.139.36', '188.20.139.39'),
  rangeIP('145.253.101.208', '145.253.101.223'),
  rangeIP('193.47.255.0', '193.47.255.255'),
  rangeIP('193.175.67.32', '193.175.67.47'),
  rangeIP('195.37.190.152', '195.37.190.187'),
  rangeIP('195.37.234.128', '195.37.234.135'),
  rangeIP('195.37.234.152', '195.37.234.159'),
  rangeIP('194.117.122.118', '194.117.122.118'),
  rangeIP('195.37.209.96', '195.37.209.99'),
  rangeIP('195.37.232.168', '195.37.232.171'),
  rangeIP('195.37.234.140', '195.37.234.143'),
  rangeIP('195.145.26.0', '195.145.26.15'),
  rangeIP('195.37.190.188', '195.37.190.191'),
  rangeIP('195.37.173.180', '195.37.173.183'),
  rangeIP('212.184.78.16', '212.184.78.23'),
  rangeIP('195.37.234.252', '195.37.234.255'),
  rangeIP('213.23.40.224', '213.23.40.231'),
  rangeIP('217.239.214.88', '217.239.214.95'),
  rangeIP('217.239.164.176', '217.239.164.183'),
  rangeIP('213.39.246.136', '213.39.246.143'),
  rangeIP('213.39.247.104', '213.39.247.111'),
  rangeIP('195.50.183.56', '195.50.183.63'),
  rangeIP('217.239.185.208', '217.239.185.223'),
  rangeIP('217.239.170.88', '217.239.170.95'),
  rangeIP('62.157.195.224', '62.157.195.239'),
  rangeIP('217.239.223.168', '217.239.223.175'),
  rangeIP('62.154.215.128', '62.154.215.143'),
  rangeIP('62.153.91.96', '62.153.91.111'),
  rangeIP('62.214.157.192', '62.214.157.199'),
  rangeIP('217.239.232.88', '217.239.232.95'),
  rangeIP('62.153.238.0', '62.153.238.15'),
  rangeIP('217.239.219.224', '217.239.219.231'),
  rangeIP('217.239.219.240', '217.239.219.247'),
  rangeIP('217.239.240.152', '217.239.240.159'),
  rangeIP('62.214.157.232', '62.214.157.239'),
  rangeIP('80.155.25.128', '80.155.25.135'),
  rangeIP('81.14.172.32', '81.14.172.47'),
  rangeIP('84.46.72.112', '84.46.72.119'),
  rangeIP('85.182.157.120', '85.182.157.127'),
  rangeIP('84.46.96.96', '84.46.96.111'),
  rangeIP('87.129.24.8', '87.129.24.15'),
  rangeIP('82.135.37.248', '82.135.37.255'),
  rangeIP('80.155.141.40', '80.155.141.47'),
  rangeIP('80.155.60.144', '80.155.60.151'),
  rangeIP('87.129.62.224', '87.129.62.231'),
  rangeIP('87.191.32.240', '87.191.32.247'),
  rangeIP('91.25.156.144', '91.25.156.151'),
  rangeIP('91.25.158.240', '91.25.158.247'),
  rangeIP('87.245.121.240', '87.245.121.247'),
  rangeIP('89.106.221.80', '89.106.221.87'),
  rangeIP('91.25.154.240', '91.25.154.247'),
  rangeIP('87.129.61.176', '87.129.61.183'),
  rangeIP('89.106.221.88', '89.106.221.95'),
  rangeIP('91.25.144.48', '91.25.144.55'),
  rangeIP('91.26.109.248', '91.26.109.255'),
  rangeIP('91.26.8.192', '91.26.8.207'),
  rangeIP('91.26.58.104', '91.26.58.111'),
  rangeIP('91.26.96.240', '91.26.96.247'),
  rangeIP('91.26.111.72', '91.26.111.79'),
  rangeIP('91.26.52.16', '91.26.52.23'),
  rangeIP('91.26.59.8', '91.26.59.15'),
  rangeIP('92.198.25.141', '92.198.25.141'),
  rangeIP('91.26.105.32', '91.26.105.39'),
  rangeIP('91.26.49.8', '91.26.49.15'),
  rangeIP('93.240.132.16', '93.240.132.31'),
  rangeIP('93.241.13.184', '93.241.13.191'),
];

const getOneSignalId = () => {
  switch (window.location.hostname) {
    case DOMAINS.MIRROR_2:
      return ONE_SIGNAL_IDS.MIRROR_2;
    case DOMAINS.MIRROR_3:
      return ONE_SIGNAL_IDS.MIRROR_3;
    case DOMAINS.MIRROR_10:
      return ONE_SIGNAL_IDS.MIRROR_10;
    case DOMAINS.MAIN:
      return ONE_SIGNAL_IDS.MAIN;
    case DOMAINS.DEV:
    default:
      return;
  }
};

export const ONE_SIGNAL_ID = getOneSignalId();

export const LIVESPINS_TENANT_ID = 'LUCKYELF';
export const LIVESPINS_RESTRICTED = [Country.Australia, Country.Canada];

export enum RestrictedProviders {
  LIVESPIN = 'livespin',
  AUTOPAY = 'autopay',
}

export const TRUELAB_PROVIDER_ID = 'truelab' as const;
